// Meeting.js — ZOOM-LIKE UX with proper peer state handling
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CLINIC_API_ORIGIN } from '../config/apiOrigins';
import { useAudioLevel } from './useAudioLevel';
import './Meeting.css';

const now = () => new Date().toISOString();

export default function Meeting() {
  const { session_uuid } = useParams();

  // UI state
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [camOn, setCamOn] = useState(false);

  // Devices
  const [mics, setMics] = useState([]);
  const [cams, setCams] = useState([]);
  const [selMicId, setSelMicId] = useState('');
  const [selCamId, setSelCamId] = useState('');

  // ICE
  const [iceServers, setIceServers] = useState([]);

  // roster + media state
  const [roster, setRoster] = useState({
    clinician: false,
    patient: false,
    states: {
      clinician: { cam: false, mic: false },
      patient: { cam: false, mic: false }
    }
  });

  // Speaking detection
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);

  // media + rtc
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);

  // logger helpers
  const log = (...a) => console.log(now(), `[MEET ${session_uuid}]`, ...a);
  const warn = (...a) => console.warn(now(), `[MEET ${session_uuid}]`, ...a);
  const err = (...a) => console.error(now(), `[MEET ${session_uuid}]`, ...a);

  const getConfig = () => {
    const raw = localStorage.getItem(`meet:${session_uuid}`);
    const cfg = raw ? JSON.parse(raw) : {};
    const role = cfg.role || 'clinician';
    const apiBase = CLINIC_API_ORIGIN;
    const servers = Array.isArray(cfg.iceServers) && cfg.iceServers.length
      ? cfg.iceServers
      : [{ urls: 'stun:stun.l.google.com:19302' }];
    const booking_uuid = cfg.booking_uuid;
    const clinician_uuid = cfg.clinician_uuid;
    log('GET_CONFIG', { role, apiBase, booking_uuid, clinician_uuid });
    return { role, apiBase, servers, booking_uuid, clinician_uuid };
  };

  const makeWsUrl = (apiBase) => {
    const u = new URL(apiBase);
    const wsProto = u.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${wsProto}://${u.host}/ws`;
    log('WS_URL', url);
    return url;
  };

  const safeSend = (obj) => {
    try {
      if (wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify(obj));
        log('WS_SEND->', obj.type, JSON.stringify(obj));
      } else {
        warn('WS_NOT_OPEN', 'readyState=', wsRef.current?.readyState, 'dropping:', obj.type);
      }
    } catch (e) {
      err('WS_SEND_FAIL', e, obj);
    }
  };

  // Get actual media state from tracks
  const getActualMediaState = () => {
    const stream = localStreamRef.current;
    if (!stream) {
      log('GET_ACTUAL_MEDIA_STATE', 'no stream');
      return { mic: false, cam: false };
    }
    
    const audioTracks = stream.getAudioTracks();
    const videoTracks = stream.getVideoTracks();
    
    const micEnabled = audioTracks.length > 0 && audioTracks[0].enabled;
    const camEnabled = videoTracks.length > 0 && videoTracks[0].enabled;
    
    log('GET_ACTUAL_MEDIA_STATE', {
      audioTracks: audioTracks.length,
      videoTracks: videoTracks.length,
      micEnabled,
      camEnabled
    });
    
    return { mic: micEnabled, cam: camEnabled };
  };

  // Audio level monitoring
  const localAudio = useAudioLevel(localStreamRef.current, micOn && joined);
  const remoteAudio = useAudioLevel(remoteRef.current?.srcObject, joined);

  useEffect(() => {
    log('LOCAL_SPEAKING_CHANGE', localAudio.isSpeaking);
    setLocalSpeaking(localAudio.isSpeaking);
  }, [localAudio.isSpeaking]);

  useEffect(() => {
    log('REMOTE_SPEAKING_CHANGE', remoteAudio.isSpeaking);
    setRemoteSpeaking(remoteAudio.isSpeaking);
  }, [remoteAudio.isSpeaking]);

  // device list
  useEffect(() => {
    (async () => {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        const micList = list.filter(d => d.kind === 'audioinput');
        const camList = list.filter(d => d.kind === 'videoinput');
        setMics(micList);
        setCams(camList);
        if (micList[0] && !selMicId) setSelMicId(micList[0].deviceId);
        if (camList[0] && !selCamId) setSelCamId(camList[0].deviceId);
        log('ENUM_DEVICES', { mics: micList.length, cams: camList.length });
      } catch (e) {
        warn('ENUM_DEVICES_FAIL', e?.message || e);
      }
    })();
  }, []);

  // join
  const handleJoin = async () => {
    log('HANDLE_JOIN_START');
    const { role, apiBase, servers } = getConfig();
    setIceServers(servers);
    setJoined(true);

    // RTCPeerConnection
    pcRef.current = new RTCPeerConnection({ iceServers: servers });
    const pc = pcRef.current;
    log('PC_CREATED', { iceServers: servers });

    pc.oniceconnectionstatechange = () => log('ICE_STATE_CHANGE', pc.iceConnectionState);
    pc.onsignalingstatechange = () => log('SIG_STATE_CHANGE', pc.signalingState);
    pc.onicegatheringstatechange = () => log('GATHER_STATE_CHANGE', pc.iceGatheringState);
    pc.onconnectionstatechange = () => log('PC_STATE_CHANGE', pc.connectionState);
    pc.onnegotiationneeded = () => log('NEGOTIATION_NEEDED');

    pc.ontrack = (e) => {
      log('ONTRACK', {
        streamId: e.streams?.[0]?.id,
        trackKind: e.track?.kind,
        trackId: e.track?.id,
        trackEnabled: e.track?.enabled
      });
      if (remoteRef.current && e.streams && e.streams[0]) {
        remoteRef.current.srcObject = e.streams[0];
        log('REMOTE_VIDEO_ATTACHED', e.streams[0].id);
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        log('ICE_LOCAL_CANDIDATE', {
          type: e.candidate.type,
          protocol: e.candidate.protocol
        });
        safeSend({ type: 'candidate', candidate: e.candidate, session_uuid });
      } else {
        log('ICE_LOCAL_GATHERING_COMPLETE');
      }
    };

    // local media
    try {
      let gumStream = null;
      if (micOn || camOn) {
        const constraints = {
          audio: micOn ? { deviceId: selMicId ? { exact: selMicId } : undefined } : false,
          video: camOn
            ? { deviceId: selCamId ? { exact: selCamId } : undefined, width: 1280, height: 720 }
            : false,
        };
        log('GUM_REQUEST', constraints);
        gumStream = await navigator.mediaDevices.getUserMedia(constraints);
        log('GUM_SUCCESS', {
          audioTracks: gumStream.getAudioTracks().length,
          videoTracks: gumStream.getVideoTracks().length
        });
      } else {
        log('GUM_SKIP', 'no mic/cam requested');
      }
      
      if (gumStream) {
        localStreamRef.current = gumStream;
        if (localRef.current) {
          localRef.current.srcObject = gumStream;
          log('LOCAL_VIDEO_ATTACHED');
        }
        gumStream.getTracks().forEach(t => {
          pc.addTrack(t, gumStream);
          log('TRACK_ADDED_TO_PC', { kind: t.kind, enabled: t.enabled });
        });
      } else {
        log('NO_LOCAL_MEDIA', 'receive-only mode');
      }
    } catch (e) {
      err('GUM_FAIL', e?.message || e);
      warn('FALLBACK_TO_RECEIVE_ONLY');
    }

    // WebSocket signaling
    const wsUrl = makeWsUrl(apiBase);
    wsRef.current = new WebSocket(wsUrl);
    log('WS_CONNECTING', wsUrl);

    wsRef.current.onopen = async () => {
      log('WS_OPEN');
      
      const joinMsg = { type: 'join', session_uuid, role };
      log('WS_SEND_JOIN', joinMsg);
      safeSend(joinMsg);
      
      // Get actual state and send it
      const actualState = getActualMediaState();
      const mediaStateMsg = { type: 'media-state', session_uuid, cam: actualState.cam, mic: actualState.mic };
      log('WS_SEND_INITIAL_MEDIA_STATE', mediaStateMsg);
      safeSend(mediaStateMsg);

      if (role === 'clinician') {
        log('CREATING_OFFER', 'as clinician');
        const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
        await pc.setLocalDescription(offer);
        log('OFFER_CREATED');
        safeSend({ type: 'offer', sdp: offer, session_uuid });
      }
    };

    wsRef.current.onmessage = async (event) => {
      try {
        const message = JSON.parse(event.data);
        log('WS_RECV<-', message.type, message);

        if (message.type === 'offer' && role === 'patient') {
          log('RECV_OFFER', 'setting remote description');
          await pc.setRemoteDescription(new RTCSessionDescription(message.sdp));
          log('REMOTE_DESC_SET', 'creating answer');
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          log('ANSWER_CREATED');
          safeSend({ type: 'answer', sdp: answer, session_uuid });
          return;
        }

        if (message.type === 'answer' && role === 'clinician') {
          log('RECV_ANSWER', 'setting remote description');
          await pc.setRemoteDescription(new RTCSessionDescription(message.sdp));
          log('REMOTE_DESC_SET');
          return;
        }

        if (message.type === 'candidate') {
          const c = message.candidate;
          log('RECV_ICE_CANDIDATE');
          try {
            await pc.addIceCandidate(c);
            log('ICE_CANDIDATE_ADDED');
          } catch (e) {
            err('ADD_ICE_CANDIDATE_FAIL', e);
          }
          return;
        }

        if (message.type === 'peer-joined') {
          log('PEER_JOINED', 'other participant joined');
          if (role === 'clinician') {
            const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
            await pc.setLocalDescription(offer);
            log('RENEGOTIATION_OFFER_CREATED');
            safeSend({ type: 'offer', sdp: offer, session_uuid });
          }
          return;
        }

        if (message.type === 'joined') {
          log('JOIN_ACK', message.role);
          return;
        }

        if (message.type === 'peer-left') {
          warn('PEER_LEFT', 'other participant disconnected');
          // Clear remote video when peer leaves
          if (remoteRef.current) {
            remoteRef.current.srcObject = null;
          }
          return;
        }

        if (message.type === 'roster') {
          log('ROSTER_UPDATE', message.roster);
          setRoster(message.roster);
          return;
        }

        if (message.type === 'media-state') {
          log('MEDIA_STATE_UPDATE', {
            role: message.role,
            cam: message.cam,
            mic: message.mic
          });
          setRoster(prev => {
            const next = {
              ...prev,
              states: {
                ...prev.states,
                [message.role]: { cam: !!message.cam, mic: !!message.mic }
              }
            };
            log('ROSTER_STATE_UPDATED', next);
            return next;
          });
          return;
        }

        warn('WS_UNKNOWN_TYPE', message.type);
      } catch (e) {
        err('WS_MSG_PARSE_FAIL', e);
      }
    };

    wsRef.current.onerror = (e) => err('WS_ERROR', e);
    wsRef.current.onclose = (e) => warn('WS_CLOSE', e?.code, e?.reason);
  };

  // cleanup
  useEffect(() => {
    return () => {
      log('CLEANUP_START');
      try {
        if (wsRef.current && wsRef.current.readyState === 1) {
          log('CLOSING_WS');
          wsRef.current.close();
        }
      } catch (e) {
        err('CLEANUP_WS_FAIL', e);
      }
      try {
        if (pcRef.current) {
          log('CLOSING_PC');
          pcRef.current.close();
        }
      } catch (e) {
        err('CLEANUP_PC_FAIL', e);
      }
      try {
        const s = localStreamRef.current || localRef.current?.srcObject;
        if (s) {
          log('STOPPING_TRACKS', s.getTracks().length);
          s.getTracks().forEach((t) => t.stop());
        }
        if (localRef.current) localRef.current.srcObject = null;
      } catch (e) {
        err('CLEANUP_TRACKS_FAIL', e);
      }
      log('CLEANUP_COMPLETE');
    };
  }, []);

  // End Call - ONLY CLINICIAN ENDS FOR EVERYONE
  const handleEndCall = async () => {
    log('END_CALL_START');
    const { role, apiBase, booking_uuid, clinician_uuid } = getConfig();

    try {
      // Close local resources
      const s = localStreamRef.current || localRef.current?.srcObject;
      if (s) {
        log('STOPPING_TRACKS_ON_END', s.getTracks().length);
        s.getTracks().forEach((t) => t.stop());
      }
      if (pcRef.current) {
        log('CLOSING_PC_ON_END');
        pcRef.current.close();
      }
      if (wsRef.current && wsRef.current.readyState === 1) {
        log('CLOSING_WS_ON_END');
        wsRef.current.close();
      }

      // ONLY CLINICIAN ENDS THE SESSION IN DB
      if (role === 'clinician' && booking_uuid && clinician_uuid) {
        const url = `${apiBase}/Clinician/consultations/bookings/${booking_uuid}/end-session`;
        log('END_SESSION_REQUEST_CLINICIAN', { booking_uuid, clinician_uuid });
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clinician_uuid }),
        });
        const data = await r.json().catch(() => ({}));
        log('END_SESSION_RESPONSE', r.status, data);
      } else if (role === 'patient') {
        log('PATIENT_LEAVING', 'Not ending session in DB, just disconnecting');
      }
    } catch (e) {
      err('END_CALL_FAIL', e);
    } finally {
      log('CLOSING_WINDOW');
      window.close();
    }
  };

  // Toggle audio
  const toggleLocalAudio = () => {
    log('TOGGLE_AUDIO_START');
    const s = localStreamRef.current;
    if (!s) {
      warn('TOGGLE_AUDIO_NO_STREAM');
      return;
    }
    const audioTracks = s.getAudioTracks();
    if (audioTracks.length === 0) {
      warn('TOGGLE_AUDIO_NO_TRACKS');
      return;
    }

    const newState = !audioTracks[0].enabled;
    log('TOGGLE_AUDIO', { newState });
    
    audioTracks.forEach(t => t.enabled = newState);

    const actualState = getActualMediaState();
    log('SENDING_MEDIA_STATE_AFTER_AUDIO_TOGGLE', actualState);
    safeSend({ type: 'media-state', session_uuid, mic: actualState.mic, cam: actualState.cam });
  };

  // Toggle video
  const toggleLocalVideo = () => {
    log('TOGGLE_VIDEO_START');
    const s = localStreamRef.current;
    if (!s) {
      warn('TOGGLE_VIDEO_NO_STREAM');
      return;
    }
    const videoTracks = s.getVideoTracks();
    if (videoTracks.length === 0) {
      warn('TOGGLE_VIDEO_NO_TRACKS');
      return;
    }

    const newState = !videoTracks[0].enabled;
    log('TOGGLE_VIDEO', { newState });
    
    videoTracks.forEach(t => t.enabled = newState);

    const actualState = getActualMediaState();
    log('SENDING_MEDIA_STATE_AFTER_VIDEO_TOGGLE', actualState);
    safeSend({ type: 'media-state', session_uuid, mic: actualState.mic, cam: actualState.cam });
  };

  // Status pill component
  const StatusPill = ({ label, state, speaking }) => (
    <div className={`pill ${speaking ? 'speaking' : ''}`}>
      <span className="pill-label">{label}</span>
      <span className={`pill-icon ${state?.mic ? 'on' : 'off'}`}>
        {state?.mic ? (speaking ? '🎙️' : '🎤') : '🔇'}
      </span>
      <span className={`pill-icon ${state?.cam ? 'on' : 'off'}`}>
        {state?.cam ? '📷' : '🚫'}
      </span>
      {speaking && <span className="speaking-indicator">🔊</span>}
    </div>
  );

  const { role } = getConfig();

  if (!joined) {
    return (
      <div className="clinician-meeting-container">
        <div className="prejoin-card">
          <h2>Check your setup</h2>
          <div className="prejoin-row">
            <label className="toggle">
              <input type="checkbox" checked={micOn} onChange={(e) => setMicOn(e.target.checked)} />
              <span>Microphone</span>
            </label>
            <select disabled={!micOn || mics.length === 0} value={selMicId} onChange={(e) => setSelMicId(e.target.value)}>
              {mics.length === 0 ? <option value="">No microphones</option> :
                mics.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>)}
            </select>
          </div>
          <div className="prejoin-row">
            <label className="toggle">
              <input type="checkbox" checked={camOn} onChange={(e) => setCamOn(e.target.checked)} />
              <span>Camera</span>
            </label>
            <select disabled={!camOn || cams.length === 0} value={selCamId} onChange={(e) => setSelCamId(e.target.value)}>
              {cams.length === 0 ? <option value="">No cameras</option> :
                cams.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</option>)}
            </select>
          </div>
          <div className="prejoin-help">
            {!micOn && !camOn ? <small>Joining receive-only. You can still see/hear the other side.</small> :
              <small>We'll request just the devices you enabled.</small>}
          </div>
          <button className="clinician-meeting-control-btn" onClick={handleJoin}>Join meeting</button>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="clinician-meeting-error">Error: {error}</div>;
  }

  // in-call screen
  const camStateMine = roster.states?.[role]?.cam;
  const micStateMine = roster.states?.[role]?.mic;
  const otherRole = role === 'clinician' ? 'patient' : 'clinician';
  const camStatePeer = roster.states?.[otherRole]?.cam;
  const micStatePeer = roster.states?.[otherRole]?.mic;
  const peerPresent = roster[otherRole];

  return (
    <div className="clinician-meeting-container">
      <div className="roster-bar">
        <StatusPill
          label={role === 'clinician' ? 'You (Clinician)' : 'You (Patient)'}
          state={{ cam: camStateMine, mic: micStateMine }}
          speaking={localSpeaking}
        />
        <StatusPill
          label={otherRole === 'clinician' ? 'Clinician' : 'Patient'}
          state={{ cam: camStatePeer, mic: micStatePeer }}
          speaking={remoteSpeaking}
        />
      </div>

      <div className="clinician-meeting-main">
        {/* Remote video with avatar fallback */}
        <div className={`video-wrapper ${remoteSpeaking ? 'speaking' : ''}`}>
          {peerPresent ? (
            <>
              <video
                ref={remoteRef}
                autoPlay
                playsInline
                className={`clinician-meeting-remote-video ${camStatePeer ? '' : 'hidden'}`}
              />
              {!camStatePeer && (
                <div className="avatar-placeholder">
                  <div className="avatar-circle">{otherRole === 'clinician' ? 'C' : 'P'}</div>
                  <div className="avatar-note">Camera off</div>
                </div>
              )}
            </>
          ) : (
            <div className="avatar-placeholder">
              <div className="avatar-circle">⏳</div>
              <div className="avatar-note">Waiting for {otherRole}...</div>
            </div>
          )}
        </div>

        {/* Local preview (PiP) */}
        <div className={`pip ${localSpeaking ? 'speaking' : ''}`}>
          <div className="pip-inner">
            <video
              ref={localRef}
              autoPlay
              playsInline
              muted
              className={`clinician-meeting-local-video ${camStateMine ? '' : 'hidden'}`}
            />
            {!camStateMine && (
              <div className="avatar-placeholder small">
                <div className="avatar-circle">You</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="clinician-meeting-controls">
        <button className="clinician-meeting-control-btn Mute" onClick={toggleLocalAudio}>
          {micStateMine ? 'Mute' : 'Unmute'}
        </button>
        <button className="clinician-meeting-control-btn Camera" onClick={toggleLocalVideo}>
          {camStateMine ? 'Stop Cam' : 'Start Cam'}
        </button>
        <button className="clinician-meeting-control-btn clinician-btn-danger HangUp" onClick={handleEndCall}>
          {role === 'clinician' ? 'End for everyone' : 'Leave'}
        </button>
      </div>
    </div>
  );
}
